import React, { useState, useEffect, useContext } from 'react';
import { Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import { FiDollarSign, FiArrowLeft, FiSave, FiTrash2 } from 'react-icons/fi';
import { FaRoute } from 'react-icons/fa';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';
import SelectStop from './../components/SelectStop';

export default function Rate({ creating }) {
    const [rate, setRate] = useState({
        id: '',
        name: '',
        price: '',
        price_month: '',
        start: {},
        end: {}
    });
    const [isLoading, setIsLoading] = useState(!creating);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const { id } = useParams();

    // Load rate
    useEffect(() => {
        if (!creating) {
            setIsLoading(true);
            tre.api('GET', `/rates/${id}`, {
                context: context,
                success: (r) => {
                    setRate(r);
                    setIsLoading(false);
                },
                error: () => setIsLoading(false)
            });
        }
    }, [creating, id, context]);

    const handleForm = (ev) => {
        setRate({ ...rate, [ev.target.name]: ev.target.value });
    };

    const save = () => {
        setIsSaving(true);
        if (creating) {
            tre.api('POST', `/companies/${context.user.company_id}/rates`, {
                context: context,
                params: rate,
                success: () => {
                    NotificationManager.success('Tarifa creada');
                    setIsSaving(false);
                    navigate('/rates');
                },
                error: () => setIsSaving(false)
            });
        } else {
            tre.api('PUT', `/rates/${rate.id}`, {
                context: context,
                params: rate,
                success: () => {
                    NotificationManager.success('Tarifa modificada');
                    setIsSaving(false);
                },
                error: () => setIsSaving(false)
            });
        }
    };

    const deleteRate = () => {
        if (!window.confirm('¿Estás seguro de eliminar esta tarifa?')) return;
        setIsDeleting(true);
        tre.api('DELETE', `/rates/${rate.id}`, {
            context: context,
            success: () => {
                NotificationManager.success('Tarifa eliminada');
                navigate('/rates');
            },
            error: () => setIsDeleting(false)
        });
    };

    const onChangeStartEnd = (idStop, startEnd) => {
        const updatedRate = {
            ...rate,
            start_id: startEnd === 1 ? idStop : rate.start_id,
            end_id: startEnd === 2 ? idStop : rate.end_id,
            start: startEnd === 1 ? { id: idStop } : rate.start,
            end: startEnd === 2 ? { id: idStop } : rate.end,
        };
        setRate(updatedRate);
    };

    // -----| Render |-----
    if (isLoading) {
        return (
            <div className="container py-4" style={{ maxWidth: '800px' }}>
                <div className="text-center py-5">
                    <Spinner animation="border" style={{ color: '#1a73e8' }} />
                    <p className="mt-3 text-muted">Cargando tarifa...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4" style={{ maxWidth: '800px' }}>
            {/* Header */}
            <div className="d-flex align-items-center mb-4">
                <Button
                    variant="link"
                    onClick={() => navigate('/rates')}
                    className="p-0 me-3"
                    style={{ color: '#5f6368' }}
                >
                    <FiArrowLeft size={24} />
                </Button>
                <div>
                    <h4 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>
                        <FiDollarSign className="me-2" style={{ color: '#1a73e8' }} />
                        {creating ? 'Nueva Tarifa' : 'Editar Tarifa'}
                    </h4>
                    {!creating && rate.name && (
                        <small className="text-muted">{rate.name}</small>
                    )}
                </div>
            </div>

            {/* Basic Info Card */}
            <Card className="mb-4">
                <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: '#e8f0fe',
                                color: '#1a73e8'
                            }}
                        >
                            <FiDollarSign size={18} />
                        </div>
                        <div>
                            <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Información de la Tarifa</h6>
                            <small className="text-muted">Nombre y precios</small>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <Row className="g-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124' }}>Nombre de la tarifa</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={rate.name || ''}
                                    onChange={handleForm}
                                    placeholder="Ej: Tarifa urbana"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124' }}>Precio por viaje</Form.Label>
                                <div className="input-group">
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        name="price"
                                        value={rate.price || ''}
                                        onChange={handleForm}
                                        placeholder="0.00"
                                    />
                                    <span className="input-group-text">€</span>
                                </div>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124' }}>Precio máximo mensual</Form.Label>
                                <div className="input-group">
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        name="price_month"
                                        value={rate.price_month || ''}
                                        onChange={handleForm}
                                        placeholder="0.00"
                                    />
                                    <span className="input-group-text">€</span>
                                </div>
                                <Form.Text className="text-muted">
                                    Límite mensual (no se cobrará más que esto)
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Route Card */}
            <Card className="mb-4">
                <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: '#e6f4ea',
                                color: '#34a853'
                            }}
                        >
                            <FaRoute size={18} />
                        </div>
                        <div>
                            <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Recorrido</h6>
                            <small className="text-muted">Paradas de origen y destino</small>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <Row className="g-3">
                        <Col md={6}>
                            <SelectStop
                                caption="Desde (origen)"
                                value={rate.start ? rate.start.id : ''}
                                onChange={(idStop) => onChangeStartEnd(idStop, 1)}
                            />
                        </Col>
                        <Col md={6}>
                            <SelectStop
                                caption="Hasta (destino)"
                                value={rate.end ? rate.end.id : ''}
                                onChange={(idStop) => onChangeStartEnd(idStop, 2)}
                            />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Actions */}
            <div className="d-flex justify-content-between">
                <Button
                    variant="light"
                    onClick={() => navigate('/rates')}
                    style={{ padding: '10px 24px' }}
                >
                    Cancelar
                </Button>
                <div className="d-flex gap-2">
                    {!creating && (
                        <Button
                            variant="outline-danger"
                            onClick={deleteRate}
                            disabled={isDeleting}
                            style={{ padding: '10px 24px' }}
                        >
                            {isDeleting ? <Spinner size="sm" className="me-2" /> : <FiTrash2 className="me-2" />}
                            Eliminar
                        </Button>
                    )}
                    <Button
                        variant="primary"
                        onClick={save}
                        disabled={isSaving}
                        style={{ padding: '10px 24px' }}
                    >
                        {isSaving ? <Spinner size="sm" className="me-2" /> : <FiSave className="me-2" />}
                        {creating ? 'Crear Tarifa' : 'Guardar Cambios'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
